import { ANONYMOUS_NODE, ANONYMOUS_NODES, GRID_SIZE_MAP, NODE_META_PROPS } from './microgrid.constants';
import {
  AnonymousData,
  BatteryData,
  Connection,
  ConverterErrors,
  CustomEdge,
  CustomNode,
  DistributionBoxData,
  EnergyMeterData,
  ErrorObject,
  GeneratorData,
  GridConnectionData,
  LoadData,
  Microgrid,
  MicrogridCompatibilityErrors,
  MicrogridEdgeErrors,
  MicrogridNodeErrors,
  NodeTypes,
  ProcessedErrorObject,
  ProjectMicrogridCompatibilityErrors,
  ProjectMicrogridErrors,
  UpdateNodeData,
} from './microgrid.types';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { ASSET_TYPES } from 'utils/constants';
import { keysToSnakeCase } from 'utils/formatters';
import { Icons } from 'greener-ui-library';

//checking for the case where timestamp is empty object
const createDateFromStamp = (timestamp?: Date) => {
  if (!timestamp) {
    return undefined;
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }

  return new Date(timestamp);
};

/** Get mappers
 * used to map the data from the API to the data we want to use in the app
 */

export const getMicrogridMapper = (microgrid: Microgrid) => {
  const { nodes, edges, tsStart, tsEnd, ...rest } = microgrid;

  return {
    nodes: nodes?.map((node: CustomNode) => getNodeMapper(node)) || [],
    edges: edges?.map((edge: CustomEdge) => getEdgeMapper(edge)) || [],
    tsStart: createDateFromStamp(tsStart),
    tsEnd: createDateFromStamp(tsEnd),
    ...rest,
  } as Microgrid;
};
export const getMicrogridsMapper = (microgrids: Microgrid[] | undefined) => {
  return microgrids?.map((item) => getMicrogridMapper(item));
};

export const getNodeMapper = (node: CustomNode) => {
  const {
    id,
    type,
    data,
    //@ts-expect-error find way to type metadata
    meta: { icon, actualType, ...meta } = {
      icon: undefined,
      actualType: undefined,
    },
  } = node;

  const finalType = type === ANONYMOUS_NODE ? actualType : type;

  const mappedData = {
    ...data,
    icon,
    connections: data?.connections.map((connection: Connection) => {
      return connection;
    }),
  };

  return {
    id,
    type: finalType,
    data: mappedData,
    ...meta,
  };
};

export const getEdgeMapper = (edge: CustomEdge) => {
  const { id, sourceId, sourceConnectionId, targetId, targetConnectionId, meta, label } = edge;

  return {
    id,
    source: sourceId,
    sourceHandle: sourceConnectionId,
    target: targetId,
    targetHandle: targetConnectionId,
    label,
    ...meta,
  };
};

/** Mutation mappers
 * used to map the microgrid info before sending it to the backend
 */
export const mutateMicrogridInfoMapper = (microgrid: Partial<Microgrid>) => {
  const values = _.pick(microgrid, ['name', 'description', 'label', 'tsStart', 'tsEnd', 'status', 'visibleToClients']);
  return keysToSnakeCase(values);
};

// used to map an entire microgrid object including the setup
export const mutateMicrogridMapper = ({ nodes, edges, ...microgrid }: Microgrid) => ({
  ...keysToSnakeCase(microgrid),
  ...(nodes && {
    //@ts-expect-error Not sure why nodes is destructured from microgrid and then accessed via microgrid
    nodes: microgrid.nodes?.map((node: Node) => mutateNodeMapper(node)),
  }),
  ...(edges && {
    //@ts-expect-error Not sure why nodes is destructured from microgrid and then accessed via microgrid
    edges: microgrid.edges?.map((edge: Edge) => mutateEdgeMapper(edge)),
  }),
});

export const mutateMicrogridsMapper = (microgrid: Microgrid[]) => {
  return microgrid.map((item) => -mutateMicrogridMapper(item));
};

export const mutateNodeMapper = (node: CustomNode) => {
  const {
    type = '',
    data: { icon, ...data },
    id,
    isNew = false,
    ...meta
  } = node;

  const isAnonymous = ANONYMOUS_NODES.includes(type);

  const typeOrAnonymous = isAnonymous ? ANONYMOUS_NODE : type;

  const mappedData = {
    ...keysToSnakeCase(data),
    connections: data.connections.map((connection) => keysToSnakeCase(connection)),
  };

  //filter the methadata by the keys we want to send to the backend
  const mappedMetaData = keysToSnakeCase(_.pick(meta, NODE_META_PROPS));

  const baseNode = {
    type: typeOrAnonymous,
    meta: {
      ...mappedMetaData,
      icon,
      actualType: isAnonymous ? type : undefined,
    },
  };

  if (isNew) {
    return {
      data: {
        ...mappedData,
        external_id: id, // we set the reactFlowId as the externalId for new nodes
      },
      ...baseNode,
    };
  } else {
    return {
      id,
      data: mappedData,
      ...baseNode,
    };
  }
};

export const mutateEdgeMapper = (edge: CustomEdge) => {
  const { id, source, target, sourceHandle, targetHandle, label, isNew = false, ...meta } = edge;

  return {
    ...((isNew && {}) || { id }), // if isNew is true, then we don't want to send the id
    source_id: source,
    source_connection_id: sourceHandle,
    target_id: target,
    target_connection_id: targetHandle,
    meta,
    label,
  };
};

/**
 * Adjust grid size based on connections
 */

export const getGridSize = (nodes: CustomNode[] = []) => {
  const maxConnections = Math.max(...nodes.map((node) => node.data.connections?.length || 0));
  if (maxConnections > 8) {
    return GRID_SIZE_MAP['l'];
  } else if (maxConnections < 4) {
    return GRID_SIZE_MAP['s'];
  } else {
    return GRID_SIZE_MAP['m'];
  }
};

/**
 * Get node icon
 */

export function getNodeIcon(nodeType: string) {
  let Icon;
  switch (nodeType.toLowerCase()) {
    case ASSET_TYPES.BATTERY: {
      Icon = Icons.GreenerBattery;
      break;
    }
    case ASSET_TYPES.GENERATOR: {
      Icon = Icons.DieselGenerator;
      break;
    }
    case ASSET_TYPES.GRID: {
      Icon = Icons.ElectricityGrid;
      break;
    }
    case ASSET_TYPES.ENERGY_METER: {
      Icon = Icons.EnergyMeter2;
      break;
    }
    case ASSET_TYPES.DISTRIBUTION_BOX: {
      Icon = Icons.DistributionBox;
      break;
    }
    case ASSET_TYPES.LOAD: {
      Icon = Icons.Construction2;
      break;
    }

    case ASSET_TYPES.SOLAR_PANEL: {
      Icon = Icons.SolarPanel;
      break;
    }

    case ASSET_TYPES.ELECTRICAL_SUBSTATION: {
      Icon = Icons.ElectricalSubstation;
      break;
    }

    case ASSET_TYPES.HYDROGEN_GENERATOR: {
      Icon = Icons.HydrogenGenerator2;
      break;
    }

    case ASSET_TYPES.WIND_TURBINE: {
      Icon = Icons.WindTurbine;
      break;
    }

    //add more end use types here
    default: {
      break;
    }
  }
  return Icon;
}

export const generateErrorMessages = (errorObject: ErrorObject, t: TFunction) => {
  const errors: string[] = [];

  const {
    microgrid_compatibility_errors = {} as MicrogridCompatibilityErrors,
    microgrid_edge_errors = {} as MicrogridEdgeErrors,
    microgrid_node_errors = {} as MicrogridNodeErrors,
    project_microgrid_compatibility_errors = {} as ProjectMicrogridCompatibilityErrors,
    project_microgrid_errors = {} as ProjectMicrogridErrors,
    converter_errors = [] as ConverterErrors,
  } = errorObject ?? {};

  const {
    greener_unit_not_registered = [],
    greener_unit_incompatible_start_date = [],
    greener_unit_incompatible_end_date = [],
  } = microgrid_compatibility_errors;

  if (greener_unit_not_registered.length) {
    greener_unit_not_registered.forEach((unit) => errors.push(t('entities:microgrid.errors.notBooked', { unit })));
  }
  if (greener_unit_incompatible_start_date.length) {
    greener_unit_incompatible_start_date.forEach((unit) =>
      errors.push(t('entities:microgrid.errors.incompatibleUnitStartDate', { unit }))
    );
  }
  if (greener_unit_incompatible_end_date.length) {
    greener_unit_incompatible_end_date.forEach((unit) =>
      errors.push(t('entities:microgrid.errors.incompatibleUnitEndDate', { unit }))
    );
  }

  const {
    connections_with_multiple_edges = [],
    edge_id_not_unique = [],
    edges_with_invalid_connections = [],
    edges_with_invalid_nodes = [],
  } = microgrid_edge_errors;

  if (connections_with_multiple_edges.length) {
    connections_with_multiple_edges.forEach((connection) =>
      errors.push(t('entities:microgrid.errors.multipleEdges', { connection }))
    );
  }
  if (edge_id_not_unique.length) {
    errors.push(t('entities:microgrid.errors.edgeNotUnique'));
  }
  if (edges_with_invalid_connections.length) {
    errors.push(t('entities:microgrid.errors.edgeHasInvalidConnection'));
  }
  if (edges_with_invalid_nodes.length) {
    edges_with_invalid_nodes.forEach((edge) =>
      errors.push(t('entities:microgrid.errors.edgeHasInvalidNodes', { edge }))
    );
  }

  const {
    microgrid_genset_node_misses_rated_apparent_power = [],
    microgrid_greener_unit_ext_id_not_unique = [],
    microgrid_grid_node_misses_rated_current = [],
    microgrid_has_no_nodes = false,
    microgrid_has_anonymous_nodes = [],
    microgrid_node_misses_greener_unit_ext_id = [],
    microgrid_nodes_not_unique = [],
    microgrid_nodes_without_edges = [],
    microgrid_genset_node_misses_relay_id = [],
    microgrid_genset_node_misses_controlled_by_greener_unit_ext_id = [],
    microgrid_genset_node_controlled_by_greener_unit_not_in_microgrid = [],
    microgrid_genset_node_misses_active_power_setting = [],
    microgrid_battery_with_second_inverter_has_no_unit_with_second_inverter = [],
  } = microgrid_node_errors;

  if (microgrid_genset_node_misses_rated_apparent_power.length) {
    errors.push(t('entities:microgrid.errors.gensetRatedApparentPower'));
  }
  if (microgrid_greener_unit_ext_id_not_unique.length) {
    microgrid_greener_unit_ext_id_not_unique.forEach((unit) =>
      errors.push(t('entities:microgrid.errors.unitNotUnique', { unit }))
    );
  }
  if (microgrid_grid_node_misses_rated_current.length) {
    errors.push(t('entities:microgrid.errors.gridMissingRatedCurrent'));
  }
  if (microgrid_has_no_nodes) {
    errors.push(t('entities:microgrid.errors.noNodes'));
  }
  if (microgrid_has_anonymous_nodes.length) {
    microgrid_has_anonymous_nodes.forEach((node) =>
      errors.push(t('entities:microgrid.errors.anonymousNode', { node }))
    );
  }
  if (microgrid_node_misses_greener_unit_ext_id.length) {
    errors.push(t('entities:microgrid.errors.nodeMissingExtId'));
  }
  if (microgrid_nodes_not_unique.length) {
    errors.push(t('entities:microgrid.errors.nodeNotUnique'));
  }
  if (microgrid_nodes_without_edges.length) {
    errors.push(t('entities:microgrid.errors.nodeHasNoEdges'));
  }
  if (microgrid_genset_node_misses_relay_id.length) {
    errors.push(t('entities:microgrid.errors.gensetRelayId'));
  }
  if (microgrid_genset_node_misses_controlled_by_greener_unit_ext_id.length) {
    errors.push(t('entities:microgrid.errors.gensetControlledBy'));
  }
  if (microgrid_genset_node_misses_active_power_setting.length) {
    errors.push(t('entities:microgrid.errors.gensetActivePower'));
  }
  if (microgrid_genset_node_controlled_by_greener_unit_not_in_microgrid.length) {
    errors.push(t('entities:microgrid.errors.gensetControlledByWrongUnit'));
  }
  if (microgrid_battery_with_second_inverter_has_no_unit_with_second_inverter.length) {
    errors.push(t('entities:microgrid.errors.secondInverter'));
  }

  const {
    incompatible_project_start_date = false,
    incompatible_project_end_date = false,
    overlapping_valid_microgrids = [],
  } = project_microgrid_compatibility_errors;

  if (incompatible_project_start_date) {
    errors.push(t('entities:microgrid.errors.incompatibleProjectStartDate'));
  }
  if (incompatible_project_end_date) {
    errors.push(t('entities:microgrid.errors.incompatibleProjectEndDate'));
  }
  if (overlapping_valid_microgrids.length) {
    overlapping_valid_microgrids.forEach((microgrid) =>
      errors.push(t('entities:microgrid.errors.overlappingMicrogridDates', { microgrid }))
    );
  }

  const { project_microgrid_invalid_dates = false } = project_microgrid_errors;

  if (project_microgrid_invalid_dates) {
    errors.push(t('entities:microgrid.errors.invalidDates'));
  }

  if (converter_errors.length) {
    errors.push(t('entities:microgrid.errors.converterError'));
  }

  return errors;
};

const microgridErrorMessages = {
  incompatible_project_start_date:
    'projects:content.overview.overviewForms.projectStartDateIncompatibleWithProjectMicrogrid',
  incompatible_project_end_date:
    'projects:content.overview.overviewForms.projectEndDateIncompatibleWithProjectMicrogrid',
  greener_unit_incompatible_start_date:
    'projects:content.overview.overviewForms.pguiStartDateIncompatibleWithProjectMicrogrid',
  greener_unit_incompatible_end_date:
    'projects:content.overview.overviewForms.pguiEndDateIncompatibleWithProjectMicrogrid',
};

export const parseMicrogridError = (errorObject) => {
  if (!errorObject || !errorObject.detail) {
    return null;
  }

  const errors: ProcessedErrorObject[] = [];
  const { detail } = errorObject;

  const errorConfig = [
    {
      category: detail.project_microgrid_compatibility_errors,
      key: 'incompatible_project_start_date',
      source: 'project',
      target: 'microgrid',
    },
    {
      category: detail.project_microgrid_compatibility_errors,
      key: 'incompatible_project_end_date',
      source: 'project',
      target: 'microgrid',
    },
    {
      category: detail.microgrid_compatibility_errors,
      key: 'greener_unit_incompatible_start_date',
      source: 'greenerUnit',
      target: 'microgrid',
    },
    {
      category: detail.microgrid_compatibility_errors,
      key: 'greener_unit_incompatible_end_date',
      source: 'greenerUnit',
      target: 'microgrid',
    },
  ];

  const processErrorField = ({ category, key, source, target }) => {
    if (category && category[key]?.length) {
      errors.push({
        field: key.includes('start_date') ? 'tsStart' : 'tsEnd',
        errorMessage: microgridErrorMessages[key],
        ids: category[key],
        source,
        target,
      });
    }
  };

  errorConfig.forEach((config) => processErrorField(config));

  return errors;
};

/**
 * Checks how the number of handles is modified and removes or adds the necessary number of connections
 **/
export const updateNumberOfConnections = (node: CustomNode, newValues: Partial<UpdateNodeData>) => {
  const connections = node?.data?.connections;

  const newConnectionsNumber = newValues?.connectionsNumber ?? 0;

  // When the new number of connections is less than the current one, remove the ones at the end of the array
  if (newConnectionsNumber < connections?.length) {
    const connectionsToDelete = connections.length - newConnectionsNumber;

    connections.splice(connections.length - connectionsToDelete, connectionsToDelete);

    return connections;
  }

  if (newConnectionsNumber > connections.length) {
    const connectionsToAdd = newConnectionsNumber - connections.length;

    return [
      ...connections,
      ...Array.from(
        // Create a new array with the length of the connections to be added, and then fill the array with the values
        Array(connectionsToAdd)
      ).map((_, i) => ({
        id: (connections.length + i).toString(),
        ratedCurrent: newValues.ratedCurrent,
      })),
    ];
  }

  return connections;
};

/**
 * When changing the number of handles that a node accepts, the existing edges need to be checked in order to know if
 * they are connected to a handle that doesn't longer exists. This function checks that and returns the edges that should
 * be removed
 **/
export const getNodeEdgesWithoutHandles = (node: CustomNode, edges: CustomEdge[]) => {
  const connectionsIds = node.data.connections.map((connection) => connection.id);
  const edgesToRemove = edges
    // Check for edges that have the modified node as either the source or target
    .filter((edge) => edge.source === node.id || edge.target === node.id)
    // Check if the targetHandle or sourceHandle do not exist in the modified node
    .filter(
      (edge) => !connectionsIds.includes(edge.sourceHandle ?? '') && !connectionsIds.includes(edge.targetHandle ?? '')
    );

  return edgesToRemove;
};

//Node type guards
export const isBatteryNode = (node: CustomNode): node is CustomNode & { data: BatteryData } => {
  return node.type === NodeTypes.BATTERY;
};

export const isAnonymousNode = (node: CustomNode): node is CustomNode & { data: AnonymousData } => {
  return ANONYMOUS_NODES.includes(node.type ?? '');
};

export const isGridNode = (node: CustomNode): node is CustomNode & { data: GridConnectionData } => {
  return node.type === NodeTypes.GRID;
};

export const isGeneratorNode = (node: CustomNode): node is CustomNode & { data: GeneratorData } => {
  return node.type === NodeTypes.DIESEL_GENSET;
};

export const isLoadNode = (node: CustomNode): node is CustomNode & { data: LoadData } => {
  return node.type === NodeTypes.LOAD;
};

export const isDistributionBoxNode = (node: CustomNode): node is CustomNode & { data: DistributionBoxData } => {
  return node.type === NodeTypes.DISTRIBUTION_BOX;
};

export const isEnergyMeterNode = (node: CustomNode): node is CustomNode & { data: EnergyMeterData } => {
  return node.type === NodeTypes.ENERGY_METER;
};
